import React, { useMemo } from 'react';
import { Box, Flex } from '@mantine/core';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import IndividualDataContentV2 from './v2/IndividualDataContentV2';
import { FamilyProfileContent } from './v2/family/FamilyProfileContent';
import { NonFinancialDataTabs } from './types';
import PersonalDetails from './sections/PersonalDetails';
import Dependants from './sections/Dependants';
import Health from './sections/Health';
import Vulnerabilities from './sections/Vulnerabilities';
import Employment from './sections/Employment';
import Objectives from './sections/Objectives';
import EstatePlanning from './sections/EstatePlanning';
import RiskAndInvestmentPreferences from './sections/RiskAndInvestmentPreferences';
import TaxAndAdviceMoments from './sections/TaxAndAdviceMoments';
import AdvisorsProfessionalConnections from './sections/AdvisorsProfessionalConnections';
import SavedMemories from './sections/SavedMemories';

export default function NonFinancialDataContent() {
  const { flagEnabledIndividualDetailsRework, flagEnabledFamilyScreenRework } = useFlags();
  const [searchParams] = useSearchParams();

  if (flagEnabledIndividualDetailsRework) {
    if (flagEnabledFamilyScreenRework && searchParams.get('view') === 'family') {
      return <FamilyProfileContent />;
    }
    return <IndividualDataContentV2 />;
  }
  return <NonFinancialDataContentLegacy />;
}

function NonFinancialDataContentLegacy() {
  const { clientId } = useCurrentClient();
  const { sectionSlug } = useParams<{ sectionSlug: string }>();

  const NON_FINANCIAL_DATA_LINKS = [
    {
      label: 'Personal details',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.PersonalDetails}`,
    },
    {
      label: 'Dependants',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.Dependants}`,
    },
    {
      label: 'Health',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.Health}`,
    },
    {
      label: 'Vulnerabilities',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.Vulnerabilities}`,
    },
    {
      label: 'Employment',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.Employment}`,
    },
    {
      label: 'Objectives',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.Objectives}`,
    },
    {
      label: 'Estate planning',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.EstatePlanning}`,
    },
    {
      label: 'Risk and investment preferences',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.RiskAndInvestmentPreferences}`,
    },
    {
      label: 'Tax and advice moments',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.TaxAndAdviceMoments}`,
    },
    {
      label: 'Key contacts',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.AdvisorsProfessionalConnections}`,
    },
    {
      label: 'Saved memories',
      href: `/client/${clientId}/non-financial-data/${NonFinancialDataTabs.SavedMemories}`,
    },
  ];
  const content = useMemo(() => {
    switch (sectionSlug) {
      case NonFinancialDataTabs.PersonalDetails:
        return <PersonalDetails />;
      case NonFinancialDataTabs.Dependants:
        return <Dependants />;
      case NonFinancialDataTabs.Health:
        return <Health />;
      case NonFinancialDataTabs.Vulnerabilities:
        return <Vulnerabilities />;
      case NonFinancialDataTabs.Employment:
        return <Employment />;
      case NonFinancialDataTabs.Objectives:
        return <Objectives />;
      case NonFinancialDataTabs.EstatePlanning:
        return <EstatePlanning />;
      case NonFinancialDataTabs.RiskAndInvestmentPreferences:
        return <RiskAndInvestmentPreferences />;
      case NonFinancialDataTabs.TaxAndAdviceMoments:
        return <TaxAndAdviceMoments />;
      case NonFinancialDataTabs.AdvisorsProfessionalConnections:
        return <AdvisorsProfessionalConnections />;
      case NonFinancialDataTabs.SavedMemories:
        return <SavedMemories />;
      default:
        return (
          <Navigate
            to={`/client/${clientId}/non-financial-data/${NonFinancialDataTabs.PersonalDetails}`}
            replace
          />
        );
    }
  }, [sectionSlug, clientId]);

  return (
    <Flex direction={'row'} justify={'stretch'}>
      <Box flex={1}>
        <PageWithSidebar
          links={NON_FINANCIAL_DATA_LINKS}
          activeUrl={`/client/${clientId}/non-financial-data/${sectionSlug}`}
        >
          {content}
        </PageWithSidebar>
      </Box>
    </Flex>
  );
}
